/**
 * mount.tsx — the ONE generic mount point for card islands.
 *
 * The vanilla page writes a placeholder and nothing else:
 *
 *   <div data-ora-card="distribution"
 *        data-ora-card-state="ok"
 *        data-ora-card-payload='{"title":"Customers", ...}'></div>
 *
 * and this hydrates it. The page keeps its route, its auth, its fetch and
 * its canonical variant lock exactly as they are. Cards are leaves.
 *
 * ONE RUNTIME. This ships inside the bundle the Orbi dock already ships in,
 * so the page loads one React and one ReactDOM. A separate card bundle would
 * have meant a second React runtime on the same document.
 *
 * SHADOW ROOT, ON PURPOSE. Each card mounts into its own shadow root, so:
 *   - Tailwind's utilities cannot touch the fourteen hand-written page
 *     stylesheets, and those stylesheets cannot touch the card;
 *   - but CSS CUSTOM PROPERTIES still inherit across the boundary, so
 *     --chart-1..5, --card, --border and the rest of the brand compiler's
 *     output reach the card unchanged. Tokens in, styles contained.
 *
 * Re-mounting is idempotent: a container that already has a root is UPDATED
 * (payload re-read, same React tree, no teardown), so a page that repaints a
 * card on a lens change does not leak roots or re-run mount effects.
 */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import cardsCss from './cards.css?inline';
import { DistributionCard, type DistributionPayload, type DistributionState } from './DistributionCard';

const VALID_STATES: DistributionState[] = [
  'loading', 'ok', 'empty', 'not-connected', 'partial',
  'stale', 'permission-limited', 'degraded', 'error'
];

type Mounted = { root: Root; host: HTMLElement };
const mounted = new WeakMap<Element, Mounted>();

function readPayload(el: Element): DistributionPayload {
  const chrome = el.getAttribute('data-ora-card-chrome') === 'host' ? 'host' as const : 'own' as const;
  const raw = el.getAttribute('data-ora-card-payload');
  if (!raw) return { title: 'Untitled', chrome };
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? { chrome, ...parsed } : { title: 'Untitled', chrome };
  } catch {
    /* A payload this island cannot parse is a real failure of the page that
       wrote it, and the operator is entitled to know that rather than to see
       a blank rectangle. It renders as the error state, saying so. */
    return { title: 'Untitled', chrome, stateHeadline: 'This card could not read its data', canRetry: false };
  }
}

function readState(el: Element, payload: DistributionPayload): DistributionState {
  const s = (el.getAttribute('data-ora-card-state') || '').trim() as DistributionState;
  if (VALID_STATES.indexOf(s) >= 0) return s;
  /* No state declared: infer only the two cases that are unambiguous, and
     never infer 'ok' from an empty series — that is exactly the donut-of-
     zeroes this card exists to refuse. */
  if (payload.stateHeadline && !payload.series?.length) return 'empty';
  return payload.series && payload.series.length ? 'ok' : 'loading';
}

function render(el: Element) {
  const payload = readPayload(el);
  const state = readState(el, payload);
  let rec = mounted.get(el);

  if (!rec) {
    const shadow = (el as HTMLElement).shadowRoot || (el as HTMLElement).attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = cardsCss;
    const host = document.createElement('div');
    /* The shadow root's own box inherits the page's font and colour scheme;
       everything inside is Tailwind's. */
    host.className = 'ora-card-root';
    shadow.appendChild(style);
    shadow.appendChild(host);
    rec = { root: createRoot(host), host };
    mounted.set(el, rec);
  }

  const retry = () => {
    /* The card does not own the fetch, so it cannot retry it. It asks the
       page to, by the same event the page already listens for. A page that
       does not listen gets a card whose retry does nothing visible, which is
       why the caller controls whether the button renders at all. */
    el.dispatchEvent(new CustomEvent('ora-card:retry', { bubbles: true, detail: { card: el.getAttribute('data-ora-card') } }));
  };

  const kind = el.getAttribute('data-ora-card');
  if (kind === 'distribution') {
    rec.root.render(<DistributionCard state={state} payload={payload} onRetry={retry} />);
  } else {
    /* An unknown card kind is named out loud rather than silently skipped:
       a placeholder that renders nothing is indistinguishable from a bug. */
    rec.root.render(
      <div className="ora-card-root rounded-xl border bg-card p-4 text-sm text-muted-foreground">
        Unknown card kind “{kind}”.
      </div>
    );
  }
}

/** Hydrate every [data-ora-card] under `scope` (default: document). */
export function mountCards(scope: ParentNode = document): number {
  const nodes = Array.from(scope.querySelectorAll('[data-ora-card]'));
  nodes.forEach(render);
  return nodes.length;
}

/** Re-render one card after the page changed its state or payload. */
export function updateCard(el: Element) {
  if (el && el.getAttribute('data-ora-card')) render(el);
}

/**
 * Watch the document for cards added or re-stated after first paint. The
 * vanilla pages repaint card bodies on lens change, time-range change and
 * poll, so a one-shot scan at load would hydrate the first render and miss
 * every one after it.
 */
export function observeCards(): MutationObserver {
  const obs = new MutationObserver((records) => {
    const touched = new Set<Element>();
    for (const r of records) {
      if (r.type === 'attributes' && r.target instanceof Element) {
        if (r.target.hasAttribute('data-ora-card')) touched.add(r.target);
      }
      r.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        if (n.hasAttribute?.('data-ora-card')) touched.add(n);
        n.querySelectorAll?.('[data-ora-card]').forEach((c) => touched.add(c));
      });
    }
    touched.forEach(render);
  });
  obs.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [
      'data-ora-card', 'data-ora-card-state', 'data-ora-card-payload', 'data-ora-card-chrome'
    ]
  });
  return obs;
}
